import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { TherapistAuthStateService } from '@therapist/shared/services/state/therapist-auth-state.service';
import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, throwError } from 'rxjs';
import { TherapistStateService } from '@therapist/shared/services/state/therapist-state.service';
import { SUBSCRIPTION_REQUIRED_CODE } from '@therapist/shared/models/subscription/therapist-access-state.enum';

@Injectable({providedIn: 'root'})
export class TherapistAuthInterceptor implements HttpInterceptor {

  readonly #authService = inject(TherapistAuthStateService);
  readonly #injector = inject(Injector);

  /** Läuft gerade eine Behandlung für "subscription-required"? Verhindert parallele Profil-Reloads. */
  #handlingLock = false;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.#authService.getAccessToken()();
    const request = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (this.#isSubscriptionRequired(error, req.url)) {
          this.#handleLockedAccess();
        }
        return throwError(() => error);
      })
    );
  }

  #isSubscriptionRequired(error: unknown, url: string): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 403
      && error.error?.code === SUBSCRIPTION_REQUIRED_CODE
      && !url.includes('/therapist/profile')
      && !url.includes('/subscription');
  }

  #handleLockedAccess(): void {
    if (this.#handlingLock) {
      return;
    }
    this.#handlingLock = true;

    // lazy, weil TherapistStateService über den ApiService selbst den HttpClient braucht
    const therapistState = this.#injector.get(TherapistStateService);
    const router = this.#injector.get(Router);

    // finalize gibt das Flag bei Erfolg und bei jedem Fehler frei
    therapistState.loadTherapistProfile$()
      .pipe(finalize(() => this.#handlingLock = false))
      .subscribe({
        next: () => router.navigate(['/subscription']),
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            this.#authService.logout();
            return;
          }

          // die Abo-Seite lädt selbst neu und zeigt ihren Fehler mit "Erneut versuchen"
          router.navigate(['/subscription']);
        }
      });
  }
}
